import { useRef, useState, useMemo, useCallback } from "react";
import Table from "components/table";
import { withTranslation } from "react-i18next";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import ThreeDots from "assets/images/threedots.svg";
import sortIcon from "assets/images/mir-ico-sort.svg";
import Text from "components/Text";
import profileStyles from "pages/Auth/components/Profile/profile.module.scss";
import { formatCalendarDate, formatLocalDateTime } from "utils";
import { staffTrackingStatusOptions } from "constants";
import moment from "moment";
import styles from "./StaffTracking.module.scss";
import officePolicystyles from "../../OfficePolicyManagement.module.scss";
import "./StaffTracking.scss";
import {
  staffTrackingReNotify,
  staffTrackingArchiveAssignments,
  cancelStaffTrackingAssignments,
} from "repositories/office-policy-management-repository";
import toast from "react-hot-toast";
import Loader from "components/Loader";
import CancelAssignmentModal from "accountOwner/pages/OfficePolicyManagement/Modals/CancelAssignmentModal";
import { useStaffTrackingSelection } from "accountOwner/pages/OfficePolicyManagement/context/StaffTrackingSelectionContext";

export const sortingVariables = {
  UserName: 1,
  PolicyTitle: 2,
  PolicyVersion: 3,
  AssignedOn: 4,
  StartDate: 5,
  DueDate: 6,
};

const PENDING_STATUS_ID =
  staffTrackingStatusOptions.find((status) => status.name === "Pending")?.id ??
  2;

const StaffTrackingTable = ({
  t,
  data = [],
  sortBy,
  isDescending,
  handleSetSortByAndIsDescending,
  isArchived,
  refetch,
}) => {
  const [isBusy, setIsBusy] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelModalAssignmentId, setCancelModalAssignmentId] = useState(null);
  const openRef = useRef(null);

  const {
    selectedAssignmentIds,
    toggleAssignment,
    selectAllAssignments,
    clearStaffSelection,
  } = useStaffTrackingSelection();

  /** Same pattern as Compliance Dashboard CurrentPolicies (select all / one row). */
  const handleSelectAll = useCallback(
    (e) => {
      const checked = e.target.checked;
      if (checked) selectAllAssignments(data);
      else clearStaffSelection();
    },
    [data, selectAllAssignments, clearStaffSelection],
  );

  const handleSelectOne = useCallback(
    (e, row) => {
      const aid = Number(row.policyAssignmentId ?? row.id);
      if (!aid) return;
      toggleAssignment(aid, e.target.checked);
    },
    [toggleAssignment],
  );

  const handleReNotify = useCallback(
    async (row) => {
      const policyId = row.policyAssignmentId;
      if (!policyId) {
        toast.error(t("officePolicyManagement.officePolicyIdNotFound"));
        return;
      }

      setIsBusy(true);
      try {
        const res = await staffTrackingReNotify([Number(policyId)]);
        if (res?.status) {
          toast.success(
            res.message || t("officePolicyManagement.renotifySent"),
          );
        }
        refetch?.();
      } catch (e) {
        toast.error(
          e?.message || t("officePolicyManagement.failedToSendRenotify"),
        );
      } finally {
        setIsBusy(false);
      }
    },
    [t, refetch],
  );

  const handleArchiveUnarchive = useCallback(
    async (row) => {
      const policyId = row.policyAssignmentId;
      if (!policyId) {
        toast.error(t("officePolicyManagement.officePolicyIdNotFound"));
        return;
      }
      setIsBusy(true);
      try {
        const res = await staffTrackingArchiveAssignments(
          [policyId],
          !isArchived,
        );
        if (res?.status) {
          toast.success(
            res.message || t("officePolicyManagement.archiveUnarchiveSuccess"),
          );
        }
        if (res?.statusCode === 200) {
          refetch?.();
        }
      } catch (e) {
        toast.error(
          e?.message || t("officePolicyManagement.archiveUnarchiveFailed"),
        );
      } finally {
        setIsBusy(false);
      }
    },
    [t, refetch, isArchived],
  );

  const handleOpenCancelModal = useCallback(
    (row) => {
      const id = row.policyAssignmentId;
      if (!id) {
        toast.error(t("officePolicyManagement.officePolicyIdNotFound"));
        return;
      }
      setCancelModalAssignmentId(Number(id));
      setCancelModalOpen(true);
    },
    [t],
  );

  const handleConfirmCancel = useCallback(
    async (assignmentIdFromModal) => {
      const id = assignmentIdFromModal ?? cancelModalAssignmentId;
      if (!id) return;
      setIsBusy(true);
      try {
        const res = await cancelStaffTrackingAssignments([id]);
        if (res?.status) {
          if (res.message) toast.success(res.message);
          refetch?.();
        }
      } catch (e) {
        toast.error(e?.message || t("officePolicyManagement.archiveFailed"));
      } finally {
        setCancelModalOpen(false);
        setCancelModalAssignmentId(null);
        setIsBusy(false);
      }
    },
    [cancelModalAssignmentId, t, refetch],
  );

  const sortAndAscDescSetter = useCallback(
    (assignSort) => {
      let newIsDescending = true;
      if (sortBy === assignSort) {
        newIsDescending = !isDescending;
      }
      handleSetSortByAndIsDescending(assignSort, newIsDescending);
    },
    [sortBy, isDescending, handleSetSortByAndIsDescending],
  );

  const columns = useMemo(
    () => [
      {
        attrs: { datatitle: "" },
        dataField: "select",
        text: "",
        headerFormatter: () => {
          const rowIds = (data || [])
            .map((r) => Number(r.policyAssignmentId ?? r.id))
            .filter(Boolean);
          const allSelected =
            rowIds.length > 0 &&
            selectedAssignmentIds.length === rowIds.length &&
            rowIds.every((id) => selectedAssignmentIds.includes(id));
          return (
            <div
              className={`ch-checkbox ${officePolicystyles["select-all-checkbox"]}`}
            >
              <label className="mb-0">
                <input
                  type="checkbox"
                  aria-label={t("officePolicyManagement.selectAll")}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <span className="text-decoration-none">&nbsp;</span>
              </label>
            </div>
          );
        },
        formatter: (cell, row) => {
          const aid = Number(row.policyAssignmentId ?? row.id);
          return (
            <div className="ch-checkbox">
              <label className="mb-0">
                <input
                  type="checkbox"
                  aria-label={t("officePolicyManagement.select")}
                  value={aid}
                  checked={aid ? selectedAssignmentIds.includes(aid) : false}
                  onChange={(e) => handleSelectOne(e, row)}
                />
                <span className="text-decoration-none">&nbsp;</span>
              </label>
            </div>
          );
        },
        sort: false,
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.staffMemberName"),
        },
        dataField: "Staff Member Name",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(sortingVariables.UserName);
                }}
              >
                <span>{t("officePolicyManagement.staffMemberName")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending && sortBy === sortingVariables.UserName
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cellContent, row) => {
          const { userFullName } = row;
          return (
            <span className="credential-table-cell">{userFullName || "-"}</span>
          );
        },
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.policyName"),
        },
        dataField: "Policy Name",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(sortingVariables.PolicyTitle);
                }}
              >
                <span>{t("officePolicyManagement.policyName")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending && sortBy === sortingVariables.PolicyTitle
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cellContent, { policyTitle }) => (
          <span className="credential-table-cell">{policyTitle || "-"}</span>
        ),
        sort: true,
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.policyVersion"),
        },
        dataField: "Policy Version",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(sortingVariables.PolicyVersion);
                }}
              >
                <span>{t("officePolicyManagement.policyVersion")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending && sortBy === sortingVariables.PolicyVersion
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cellContent, { policyVersion }) => (
          <span className="credential-table-cell">{policyVersion || "-"}</span>
        ),
        sort: true,
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.assignedOn"),
        },
        dataField: "Assigned On",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(sortingVariables.AssignedOn);
                }}
              >
                <span>{t("officePolicyManagement.assignedOn")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending && sortBy === sortingVariables.AssignedOn
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cellContent, { assignedOn }) => {
          return (
            <span className={""}>
              {assignedOn
                ? formatLocalDateTime(assignedOn, "MMM DD, YYYY")
                : "-"}
            </span>
          );
        },
        sort: true,
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.startDate"),
        },
        dataField: "Start Date",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(sortingVariables.StartDate);
                }}
              >
                <span>{t("officePolicyManagement.startDate")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending && sortBy === sortingVariables.StartDate
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cellContent, { startDate }) => (
          <span className={""}>
            {startDate ? formatCalendarDate(startDate, "MMM DD, YYYY") : "-"}
          </span>
        ),
        sort: true,
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.dueDate"),
        },
        dataField: "Due Date",
        headerFormatter: () => {
          return (
            <div className="d-flex align-items-center gap-xs mb-1">
              <button
                className={
                  "reset-btn flex-shrink-null " + styles["th-btn-wrapper"]
                }
                onClick={() => {
                  sortAndAscDescSetter(sortingVariables.DueDate);
                }}
              >
                <span>{t("officePolicyManagement.dueDate")}</span>
                <img
                  src={sortIcon}
                  alt="sortIcon"
                  style={{
                    transform:
                      isDescending && sortBy === sortingVariables.DueDate
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                  }}
                />
              </button>
            </div>
          );
        },
        formatter: (cellContent, { dueDate }) => (
          <span className={""}>
            {dueDate ? formatCalendarDate(dueDate, "MMM DD, YYYY") : "-"}
          </span>
        ),
        sort: true,
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.status"),
        },
        dataField: "status",
        headerFormatter: () => {
          return (
            <div
              className="d-flex align-items-center gap-xs mb-1"
              style={{ height: "24px" }}
            >
              <span>{t("officePolicyManagement.status")}</span>
            </div>
          );
        },
        formatter: (cellContent, row) => {
          const statusId = row.status;
          const status = staffTrackingStatusOptions.find(
            (s) => s.id === statusId,
          );
          const statusName = status?.name || "-";

          const statusClassMap = {
            Pending: "Pending",
            Accepted: "Accepted",
            Started: "Started",
            Completed: "Completed",
            "In Progress": "InProgress",
            Rejected: "Rejected",
            Cancelled: "Cancelled",
            Draft: "Cancelled",
          };
          const statusClass = statusClassMap[statusName] || "Pending";
          const translatedStatusName = statusName;

          return (
            <span
              className={
                profileStyles["status"] + " " + officePolicystyles[statusClass]
              }
            >
              {translatedStatusName}
            </span>
          );
        },
      },
      {
        attrs: {
          datatitle: t("officePolicyManagement.actions"),
        },
        dataField: "Actions",
        headerFormatter: () => {
          return (
            <div
              className="d-flex align-items-center gap-xs mb-1"
              style={{ height: "24px" }}
            >
              <span>{t("officePolicyManagement.actions")}</span>
            </div>
          );
        },
        formatter: (cellContent, row) => {
          const isPending = row?.status === PENDING_STATUS_ID;
          return (
            <div className="actions-status-box">
              <UncontrolledDropdown>
                <DropdownToggle
                  caret={false}
                  tag="div"
                  className="cursor-pointer"
                >
                  <div className={`ellipsis-icon`}>
                    <img src={ThreeDots} alt="ThreeDots" />
                  </div>
                </DropdownToggle>
                <DropdownMenu left container={openRef}>
                  {isPending && (
                    <DropdownItem onClick={() => handleReNotify(row)}>
                      <Text className={"office-dropdown-item"}>
                        {t("officePolicyManagement.renotify")}
                      </Text>
                    </DropdownItem>
                  )}
                  {isArchived ? (
                    <DropdownItem onClick={() => handleArchiveUnarchive(row)}>
                      <Text className={"office-dropdown-item"}>
                        {t("officePolicyManagement.unarchive")}
                      </Text>
                    </DropdownItem>
                  ) : (
                    <DropdownItem onClick={() => handleArchiveUnarchive(row)}>
                      <Text className={"office-dropdown-item"}>
                        {t("officePolicyManagement.archive")}
                      </Text>
                    </DropdownItem>
                  )}
                  {isPending && (
                    <DropdownItem onClick={() => handleOpenCancelModal(row)}>
                      <Text className={"office-dropdown-item"}>
                        {t("officePolicyManagement.cancel")}
                      </Text>
                    </DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        },
      },
    ],
    [
      t,
      data,
      selectedAssignmentIds,
      handleSelectAll,
      handleSelectOne,
      sortBy,
      isDescending,
      sortAndAscDescSetter,
      handleReNotify,
      handleArchiveUnarchive,
      handleOpenCancelModal,
      isArchived,
    ],
  );

  return (
    <>
      {isBusy && <Loader />}
      <div className={"test-table-box ml-3 " + styles["staff-listing-table"]}>
        <div className="actions-status-box" ref={openRef} />
        <div className={officePolicystyles.compliance_table_scroll}>
          <Table
            key={`staff-tracking-table-${selectedAssignmentIds.join("-")}`}
            keyField="id"
            className="change-heading"
            tableClassName={officePolicystyles.compliance_policy_table}
            data={data}
            columns={columns}
            bordered={false}
            disablePagination
          />
        </div>
      </div>
      <CancelAssignmentModal
        isOpen={cancelModalOpen}
        toggle={() => {
          if (!isBusy) {
            setCancelModalOpen(false);
            setCancelModalAssignmentId(null);
          }
        }}
        onConfirm={handleConfirmCancel}
        assignmentId={cancelModalAssignmentId}
      />
    </>
  );
};
export default withTranslation()(StaffTrackingTable);
